import { Link } from 'react-router-dom';
import { useMemo } from 'react';
import { useLocale } from '@/contexts/LocaleContext';
import { Button } from '@/components/ui/button';
import { PublicLayout } from '@/components/layout/PublicLayout';
import { Palette, Users, MapPin, Star, Briefcase, DollarSign, Activity } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import paletteLogo from '@/assets/palette-logo.png';
import { HomeSearch } from '@/components/home/HomeSearch';
import { useCategories } from '@/hooks/useCategories';
import { useActivities } from '@/hooks/useActivities';
import { getCategoryIcon, getBrandColorByIndex } from '@/lib/categoryStyles';
import { FavoriteButton } from '@/components/FavoriteButton';
import { TiltLogo } from '@/components/TiltLogo';
import { ActivityCarouselRow } from '@/components/home/ActivityCarouselRow';
import { HeroHybrid } from '@/components/home/HeroHybrid';
import { PlaceTypesSection } from '@/components/home/PlaceTypesSection';
import { HostsCarousel } from '@/components/home/HostsCarousel';
import { TodayTomorrowSchoolsTabs } from '@/components/home/TodayTomorrowSchoolsTabs';
import FlashDealsSection from '@/components/home/FlashDealsSection';
import GatheringsSection from '@/components/home/GatheringsSection';
import { 
  useHomePageSettings, 
  useTestimonials, 
} from '@/hooks/useHomePageContent';

export default function Index() {
  const { t, locale } = useLocale();
  const isArabic = locale === 'ar';
  const { user } = useAuth();

  const execMap: Record<string, { path: string; ar: string; en: string; icon: any; color: string }> = {
    'cfo@palettekw.com': { path: '/cfo', ar: 'المدير المالي', en: 'CFO', icon: DollarSign, color: '#10b981' },
    'coo@palettekw.com': { path: '/coo', ar: 'مدير العمليات', en: 'COO', icon: Activity, color: '#3b82f6' },
    'palette@palettekw.com': { path: '/ceo', ar: 'المدير التنفيذي', en: 'CEO', icon: Briefcase, color: '#5E4FA2' },
  };
  const execProfile = user?.email ? execMap[user.email.toLowerCase()] : undefined;
  const ExecIcon = execProfile?.icon;
  
  // Fetch all dynamic content
  const { data: settings } = useHomePageSettings();
  const { data: categories = [], isLoading: categoriesLoading } = useCategories();
  const { data: activities = [], isLoading: activitiesLoading } = useActivities();
  const { data: testimonials = [] } = useTestimonials(true);
  

  // Featured categories — controlled from admin via is_featured flag
  const featuredCategories = useMemo(
    () =>
      categories
        .filter((c) => (c as any).is_featured)
        .sort((a, b) => (a.display_order || 0) - (b.display_order || 0)),
    [categories]
  );


  // Get localized content from settings
  const heroTitle = isArabic ? settings?.hero_title_ar : settings?.hero_title_en;
  const heroSubtitle = isArabic ? settings?.hero_subtitle_ar : settings?.hero_subtitle_en;
  const heroBadge = isArabic ? settings?.hero_badge_text_ar : settings?.hero_badge_text_en;
  const ctaPrimaryText = isArabic ? settings?.hero_cta_primary_text_ar : settings?.hero_cta_primary_text_en;
  const ctaSecondaryText = isArabic ? settings?.hero_cta_secondary_text_ar : settings?.hero_cta_secondary_text_en;

  return (
    <PublicLayout>
      <div className="bg-gradient-hero">
      {/* Hero Hybrid — full-bleed image carousel under header */}
      <HeroHybrid />

      {/* Today / Tomorrow tabs + AI Search (side-by-side) */}
      <TodayTomorrowSchoolsTabs />


      {/* Flash Deals — عروض اليوم */}
      <FlashDealsSection />





      {/* Featured Activities — one row per featured category */}
      {settings?.show_activities_section !== false && (
        <div className="pt-2 md:pt-4">
          {activitiesLoading ? (
            <div className="container mx-auto px-4">
              <div className="flex gap-3 overflow-hidden">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="flex-1 min-w-0 animate-pulse">
                    <div className="aspect-square bg-muted rounded-2xl" />
                    <div className="h-4 bg-muted rounded w-3/4 mt-2" />
                    <div className="h-3 bg-muted rounded w-1/2 mt-1" />
                  </div>
                ))}
              </div>
            </div>
          ) : (
            featuredCategories.map((cat, idx) => {
              const catActivities = activities.filter((a) => a.category_id === cat.id);
              if (catActivities.length === 0) return null;
              const title = isArabic ? cat.name_ar : (cat.name_en || cat.name_ar);
              return (
                <ActivityCarouselRow
                  key={cat.id}
                  title={title}
                  viewAllHref={`/activities?category=${cat.id}`}
                  activities={catActivities}
                  categories={categories}
                  eager={idx === 0}
                />
              );
            })
          )}
        </div>
      )}

      {/* Place Types Section — مكانك المثالي */}
      <PlaceTypesSection />

      {/* Categories Section */}


      {/* Hosts Carousel — منو ياينه هالحزة؟ */}
      <HostsCarousel />

      {/* Categories Section */}
      {settings?.show_categories_section !== false &&
        categories.some((c) => (c as any).is_featured && c.name_ar !== 'حكواتي') && (
        <section className="py-6 md:py-8">
          <div className="container mx-auto px-4">
            <div className="flex items-center justify-between mb-3 md:mb-4">
              <h2 className="text-2xl md:text-3xl font-bold">{t('categories')}</h2>
              <Button variant="ghost" asChild>
                <Link to="/categories">{t('viewAll')} →</Link>
              </Button>
            </div>
            {categoriesLoading ? (
              <div className="grid grid-cols-4 md:grid-cols-6 lg:grid-cols-5 gap-4">
                {[1, 2, 3, 4, 5].map((i) => (
                  <div key={i} className="bg-card rounded-2xl p-6 shadow-soft animate-pulse">
                    <div className="w-16 h-16 bg-muted rounded-2xl mx-auto mb-4" />
                    <div className="h-4 bg-muted rounded w-3/4 mx-auto" />
                  </div>
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-4 md:grid-cols-6 lg:grid-cols-5 gap-3 md:gap-4">
                {categories
                  .filter(c => (c as any).is_featured && c.name_ar !== 'حكواتي')
                  .sort((a, b) => (a.display_order || 0) - (b.display_order || 0))
                  .slice(0, 12)
                  .map((cat, i) => {
                  const IconComponent = getCategoryIcon(cat.icon);
                  const categoryName = locale === 'ar' ? cat.name_ar : (cat.name_en || cat.name_ar);
                  
                  return (
                    <Link
                      key={cat.id}
                      to={`/activities?category=${cat.id}`}
                      className={`${getBrandColorByIndex(i)} aspect-square rounded-2xl p-3 md:p-4 flex flex-col items-center justify-center text-center shadow-card hover:shadow-elevated transition-all duration-300 hover:-translate-y-1 hover:scale-[1.03] group animate-fade-in relative overflow-hidden`}
                      style={{ animationDelay: `${i * 50}ms` }}
                    >
                      {/* Subtle inner highlight */}
                      <div className="absolute inset-0 bg-gradient-to-br from-white/20 to-transparent pointer-events-none" />
                      <IconComponent className="w-6 h-6 md:w-9 md:h-9 text-white drop-shadow-md mb-1 md:mb-1.5 group-hover:scale-110 transition-transform relative z-10 shrink-0" />
                      <span className="font-bold text-[12px] md:text-sm text-white drop-shadow-md leading-tight relative z-10 px-0.5 break-words">{categoryName}</span>
                    </Link>
                  );
                })}
              </div>
            )}
          </div>
        </section>
      )}

      {/* Gathering Subscriptions — اشتراك اليمعات */}
      <GatheringsSection />

      {/* Logo + headline block (moved under Categories) */}
      <section className="relative flex items-center overflow-hidden pt-2 pb-4 md:pt-4 md:pb-6">
        <div className="absolute inset-0 opacity-40">
          <div className="absolute top-20 right-20 w-72 h-72 bg-primary/30 rounded-full blur-3xl float" />
          <div className="absolute bottom-20 left-20 w-96 h-96 bg-lavender/50 rounded-full blur-3xl float" style={{ animationDelay: '2s' }} />
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-2xl mx-auto text-center">
            <div className="flex justify-center mb-4 md:mb-6 animate-fade-in">
              <TiltLogo
                src={paletteLogo}
                alt="Palette Kuwait logo"

                className="w-32 h-32 md:w-44 md:h-44 object-contain drop-shadow-xl select-none"
              />
            </div>

            {execProfile && ExecIcon && (
              <div className="flex justify-center mb-4 animate-fade-in">
                <Link
                  to={execProfile.path}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-bold text-white shadow-lg hover:opacity-90 transition-opacity"
                  style={{ backgroundColor: execProfile.color }}
                >
                  <ExecIcon className="w-4 h-4" />
                  {isArabic ? execProfile.ar : execProfile.en}
                </Link>
              </div>
            )}

            <h1 className="text-2xl md:text-4xl font-extrabold text-foreground mb-3 leading-tight animate-fade-in animation-delay-100">
              {heroTitle || t('heroTitle')}
            </h1>

            <p className="text-sm md:text-lg text-muted-foreground mb-6 animate-fade-in animation-delay-200">
              {heroSubtitle || t('heroSubtitle')}
            </p>

            <div className="flex flex-row gap-2 justify-center animate-fade-in animation-delay-300">
              <Button size="sm" variant="outline" className="rounded-full text-xs md:text-sm px-4 bg-white/40 backdrop-blur-xl border-white/60 hover:bg-white/50" asChild>
                <Link to={settings?.hero_cta_secondary_link || '/polls/create'}>
                  {ctaSecondaryText || (isArabic ? 'صوّت' : 'Vote')}
                </Link>
              </Button>
              <Button size="sm" variant="outline" className="rounded-full text-xs md:text-sm px-4 bg-white/40 backdrop-blur-xl border-white/60 hover:bg-white/50" asChild>
                <Link to="/schools">
                  {isArabic ? 'المدارس' : 'Schools'}
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>




      {/* How it works */}
      {settings?.show_how_it_works_section !== false && (
        <section className="pt-6 md:pt-10 pb-10 md:pb-14">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-xl md:text-3xl font-bold mb-6 md:mb-8">
              {isArabic ? 'كيف يعمل باليت؟' : 'How Palette Works'}
            </h2>
            <div className="grid grid-cols-3 gap-3 md:gap-8 max-w-4xl mx-auto">
              {[
                { icon: Palette, title: isArabic ? 'اختر نشاطك' : 'Choose Activity', desc: isArabic ? 'تصفح مجموعة متنوعة من الأنشطة الممتعة' : 'Browse a variety of fun activities', useLogo: true },
                { icon: MapPin, title: isArabic ? 'حدد مكانك' : 'Set Location', desc: isArabic ? 'المدرب يأتي إلى بيتك أو ديوانيتك' : 'The instructor comes to your home', iconColor: 'text-emerald-500' },
                { icon: Users, title: isArabic ? 'استمتع مع عائلتك' : 'Enjoy with Family', desc: isArabic ? 'اكسر روتينك مع تجربة لا تُنسى' : 'Break your routine with an unforgettable experience', iconColor: 'text-purple-500' },
              ].map((step: any, i: number) => (
                <div key={i} className="animate-fade-in" style={{ animationDelay: `${i * 150}ms` }}>
                  {step.useLogo ? (
                    <div className="w-10 h-10 md:w-20 md:h-20 mx-auto mb-2 md:mb-4">
                      <img src={paletteLogo} alt="Palette Kuwait logo" className="w-full h-full object-contain" />
                    </div>
                  ) : (
                    <div className="w-10 h-10 md:w-20 md:h-20 flex items-center justify-center mx-auto mb-2 md:mb-4">
                      <step.icon className={`w-6 h-6 md:w-12 md:h-12 ${step.iconColor}`} />
                    </div>
                  )}
                  <h3 className="text-xs md:text-lg font-bold mb-1 md:mb-2">{step.title}</h3>
                  <p className="text-[11px] md:text-base text-muted-foreground leading-snug">{step.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Testimonials Section */}
      {settings?.show_testimonials_section !== false && testimonials.length > 0 && (
        <section className="py-10 md:py-14 bg-muted/50">
          <div className="container mx-auto px-4">
            <h2 className="text-2xl md:text-3xl font-bold text-center mb-8">
              {isArabic ? 'آراء عملائنا' : 'What Our Customers Say'}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
              {testimonials.slice(0, 3).map((testimonial, i) => (
                <div 
                  key={testimonial.id} 
                  className="bg-white rounded-2xl p-6 shadow-soft animate-fade-in"
                  style={{ animationDelay: `${i * 100}ms` }}
                >
                  <div className="flex items-center gap-1 mb-4">
                    {[...Array(5)].map((_, starIdx) => (
                      <Star 
                        key={starIdx} 
                        className={`w-4 h-4 ${starIdx < testimonial.rating ? 'text-yellow-400 fill-yellow-400' : 'text-muted'}`} 
                      />
                    ))}
                  </div>
                  <p className="text-muted-foreground mb-4 line-clamp-4">
                    "{isArabic ? testimonial.content_ar : (testimonial.content_en || testimonial.content_ar)}"
                  </p>
                  <div className="flex items-center gap-3">
                    {testimonial.avatar_url ? (
                      <img 
                        src={testimonial.avatar_url} 
                        alt={testimonial.author_name_ar}
                        className="w-10 h-10 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                        <span className="text-primary font-bold">
                          {(isArabic ? testimonial.author_name_ar : testimonial.author_name_en)?.[0]}
                        </span>
                      </div>
                    )}
                    <div>
                      <p className="font-medium text-sm">
                        {isArabic ? testimonial.author_name_ar : (testimonial.author_name_en || testimonial.author_name_ar)}
                      </p>
                      {(testimonial.author_role_ar || testimonial.author_role_en) && (
                        <p className="text-xs text-muted-foreground">
                          {isArabic ? testimonial.author_role_ar : (testimonial.author_role_en || testimonial.author_role_ar)}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}
      </div>
    </PublicLayout>
  );
}
